// Lógica pura do player de treino: sem React, sem rede, testável sozinha.
//
// O que o servidor devolve (WorkoutPlayerDto, Bloco 3) é a verdade; aqui só se interpreta: que campos mostrar para cada tipo de registo,
// que valores propor numa série, quanto dura o treino, e como se junta o que ainda não chegou ao servidor (a fila local) ao que já lá está.

// ── tipos de registo ───────────────────────────────────────────────────────────

/** weight-reps | reps | time | distance → o que o ecrã da série pede. */
export const trackingKind = (trackingType) => {
  switch (trackingType) {
    case 'reps': return { type: 'reps', usesReps: true, usesWeight: false, optionalWeight: true, usesTime: false };
    case 'time': return { type: 'time', usesReps: false, usesWeight: false, optionalWeight: false, usesTime: true };
    // O servidor ainda não guarda distância: um exercício "distance" regista-se pelo tempo (e diz-se no ecrã).
    case 'distance': return { type: 'distance', usesReps: false, usesWeight: false, optionalWeight: false, usesTime: true };
    default: return { type: 'weight-reps', usesReps: true, usesWeight: true, optionalWeight: false, usesTime: false };
  }
};

export const trackingLabel = (trackingType) => ({
  'weight-reps': 'Carga e repetições', reps: 'Repetições', time: 'Tempo', distance: 'Distância (registada em tempo)',
}[trackingType] || 'Carga e repetições');

// ── formatação ─────────────────────────────────────────────────────────────────

export const pad2 = (n) => String(Math.max(0, Math.trunc(n))).padStart(2, '0');

/** 75 → "1:15"; 3725 → "1:02:05". */
export const formatClock = (totalSeconds) => {
  const s = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}:${pad2(m)}:${pad2(s % 60)}` : `${m}:${pad2(s % 60)}`;
};

/** 3725 → "1 h 02 min"; 2700 → "45 min"; 40 → "40 s". */
export const formatDuration = (totalSeconds) => {
  const s = Math.max(0, Math.round(Number(totalSeconds) || 0));
  if (s < 60) return `${s} s`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return h > 0 ? `${h} h ${pad2(m)} min` : `${m} min`;
};

/** 42.5 → "42,5"; 40 → "40" (vírgula decimal, como se escreve em português). */
export const formatKg = (kg) => {
  if (kg === null || kg === undefined || kg === '' || Number.isNaN(Number(kg))) return '';
  return String(Math.round(Number(kg) * 100) / 100).replace('.', ',');
};

/** "42,5" ou "42.5" → 42.5; vazio ou inválido → null. */
export const parseDecimal = (text) => {
  if (text === null || text === undefined) return null;
  const t = String(text).trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

export const parseIntSafe = (text) => {
  const n = parseDecimal(text);
  return n === null ? null : Math.trunc(n);
};

export const formatReps = (p) => {
  if (!p || p.repsMin == null) return '';
  const hi = p.repsMax ?? p.repsMin;
  return hi > p.repsMin ? `${p.repsMin}–${hi}` : `${p.repsMin}`;
};

/** A linha de prescrição para o cabeçalho do exercício, em partes (para mostrar como "chips"). */
export const prescriptionParts = (exercise) => {
  const p = exercise?.prescription || {};
  const kind = trackingKind(exercise?.trackingType);
  const parts = [];
  const reps = formatReps(p);
  if (p.setsTarget != null) parts.push({ key: 'sets', label: 'Séries', value: String(p.setsTarget) });
  if (reps) parts.push({ key: 'reps', label: kind.usesTime ? 'Tempo' : 'Reps', value: kind.usesTime ? `${reps} s` : reps });
  if (p.targetWeightKg != null && kind.usesWeight) parts.push({ key: 'kg', label: 'Carga', value: `${formatKg(p.targetWeightKg)} kg` });
  if (p.restSeconds != null) parts.push({ key: 'rest', label: 'Descanso', value: `${p.restSeconds} s` });
  if (p.tempo) parts.push({ key: 'tempo', label: 'Cadência', value: p.tempo });
  return parts;
};

/**
 * A linha de prescrição de UM exercício, como o atleta a lê numa só linha: «4 séries · 8 reps · 60 kg».
 * Sem carga não se escreve carga nenhuma — nem zero nem «kg» vazio (sem histórico não há carga a mostrar).
 * Sem campos estruturados resta o resumo que o servidor já escreveu (nunca se inventa o que ele não disse).
 */
export const exerciseLine = (exercise) => {
  const p = exercise?.prescription || {};
  const kind = trackingKind(exercise?.trackingType);
  const reps = formatReps(p);
  const sets = p.setsTarget != null ? plural(p.setsTarget, 'série', 'séries') : null;
  if (sets === null && reps === '') return p.summary || '';

  const parts = [];
  if (sets) parts.push(sets);
  if (reps) parts.push(kind.usesTime ? `${reps} s` : `${reps} reps`);
  const kg = Number(p.targetWeightKg);
  if (p.targetWeightKg != null && Number.isFinite(kg) && kg > 0 && (kind.usesWeight || kind.optionalWeight)) parts.push(`${formatKg(kg)} kg`);
  return parts.join(' · ');
};

/**
 * Os MÚSCULOS distintos de um conjunto de exercícios do player: os grupos que o servidor mandou na biblioteca
 * de cada um (e, quando um exercício não tem grupo, os músculos principais dele). É uma contagem do que lá
 * está: sem dados devolve lista vazia e o ecrã não escreve um número nenhum.
 */
export const muscleGroups = (exercises) => {
  const groups = new Set();
  for (const e of exercises || []) {
    const lib = e?.library || {};
    if (lib.groupLabel) { groups.add(lib.groupLabel); continue; }
    for (const m of lib.muscles || []) if (m.name && (!m.role || m.role === 'primary')) groups.add(m.name);
  }
  return [...groups];
};

// ── séries: o que propor e como contar ─────────────────────────────────────────

export const MAX_SETS = 50;

/** Uma série de AQUECIMENTO: está gravada, mas fora do trabalho da sessão (não conta em nada). */
export const isWarmupSet = (set) => !!set?.isWarmup;

/** As séries de aquecimento de um exercício (as de trabalho são todas as outras). */
export const warmupSets = (exercise) => (exercise?.sets || []).filter(isWarmupSet);

/** As séries de TRABALHO: só estas contam para as séries registadas, o volume, o recorde e a sugestão. */
export const workSets = (exercise) => (exercise?.sets || []).filter((s) => !isWarmupSet(s));

// ── aquecimento proposto ──────────────────────────────────────────────────────
//
// Regra única (explicável a quem treina e escrita no relatório): só para exercícios COM CARGA prescrita
// (weight-reps e targetWeightKg > 0). Três séries a 40 %, 60 % e 80 % da carga de trabalho, com 8, 5 e 3
// repetições, cada uma arredondada aos 2,5 kg mais próximos (os discos que existem). Some-se a série quando o
// valor arredondado é 0, não desce abaixo da carga de trabalho ou repete uma já proposta — nunca se inventam
// séries de aquecimento iguais às de trabalho. Sem carga não há aquecimento: não se aquece «0 kg».

export const WARMUP_PERCENTS = [0.4, 0.6, 0.8];
export const WARMUP_REPS = [8, 5, 3];
export const WARMUP_STEP_KG = 2.5;

/** Arredonda aos 2,5 kg mais próximos (meio para baixo, como nas placas reais). */
export const roundToPlate = (kg) => Math.round((Number(kg) || 0) / WARMUP_STEP_KG) * WARMUP_STEP_KG;

/**
 * As séries de aquecimento propostas para um exercício: [{ setIndex, percent, reps, weightKg }].
 * Lista vazia quando o exercício não tem carga prescrita (nada a propor).
 */
export const warmupPlan = (exercise) => {
  const kind = trackingKind(exercise?.trackingType);
  const working = Number(exercise?.prescription?.targetWeightKg);
  if (!kind.usesWeight || !Number.isFinite(working) || working <= 0) return [];

  const plan = [];
  WARMUP_PERCENTS.forEach((percent, i) => {
    const weightKg = roundToPlate(working * percent);
    if (weightKg <= 0 || weightKg >= working) return;
    if (plan.some((p) => p.weightKg === weightKg)) return;
    plan.push({ setIndex: plan.length + 1, percent, reps: WARMUP_REPS[i], weightKg });
  });
  return plan;
};

// ── superset / circuito ───────────────────────────────────────────────────────

/** A letra do grupo: 1 → A, 2 → B… (o servidor guarda um número; o ecrã escreve A1/A2). */
export const groupLetter = (group) => (group >= 1 && group <= 26 ? String.fromCharCode(64 + group) : '?');

/**
 * A posição de um exercício no seu superset: { group, letter, position, total, label, isLast } ou null.
 * `label` é o que se escreve no ecrã: A1, A2, A3.
 */
export const supersetOf = (exercises, index) => {
  const exercise = exercises?.[index];
  const group = exercise?.supersetGroup;
  if (!group) return null;
  const members = exercises.filter((e) => e.supersetGroup === group);
  const position = members.findIndex((e) => e === exercise || e.id === exercise.id) + 1;
  if (members.length < 2 || position < 1) return null;
  return { group, letter: groupLetter(group), position, total: members.length, label: `${groupLetter(group)}${position}`, isLast: position === members.length };
};

/**
 * O descanso DEPOIS de uma série deste exercício. Num superset não se descansa entre os exercícios do grupo: a
 * seguir vem o seguinte (A1 → A2) e o descanso é no fim da ronda (no último do grupo). Fora de um superset, o
 * descanso é o que o plano prescreve.
 */
export const restAfterSet = (exercises, index, setIndex) => {
  const exercise = exercises?.[index];
  const rest = Number(exercise?.prescription?.restSeconds) || 0;
  const group = supersetOf(exercises, index);
  if (!group) return rest;
  return group.isLast ? rest : 0;   // A1 → A2 sem descanso; no fim da ronda (último do grupo) descansa-se
};

/** O nome do exercício que vem a seguir na ronda (para o ecrã dizer «sem descanso: segue A2»). */
export const nextInGroup = (exercises, index) => {
  const group = supersetOf(exercises, index);
  if (!group || group.isLast) return null;
  return exercises.slice(index + 1).find((e) => e.supersetGroup === group.group) || null;
};

/** O número de linhas de série a mostrar: as previstas, ou as já feitas se forem mais (séries extra). */
export const plannedSetCount = (exercise) => {
  const target = exercise?.prescription?.setsTarget ?? 3;
  const logged = workSets(exercise).reduce((max, s) => Math.max(max, s.setIndex || 0), 0);
  return Math.min(MAX_SETS, Math.max(target, logged));
};

/** A próxima série por registar (a primeira em falta), ou null se todas as previstas estão feitas. */
export const nextSetIndex = (exercise) => {
  const done = new Set(workSets(exercise).map((s) => s.setIndex));
  const count = plannedSetCount(exercise);
  for (let i = 1; i <= count; i += 1) if (!done.has(i)) return i;
  return null;
};

/** O índice para uma série extra (a seguir à maior já registada). */
export const extraSetIndex = (exercise) => {
  const max = workSets(exercise).reduce((m, s) => Math.max(m, s.setIndex || 0), 0);
  return Math.min(MAX_SETS, max + 1);
};

const lastSetAt = (last, index) => (last?.sets || []).find((s) => s.setIndex === index) || null;

/**
 * Os valores com que a série abre, por ordem de utilidade: o que o sócio fez nessa série da última vez, o que acabou de fazer
 * na série anterior (hoje), a carga prevista no plano. As repetições, as do plano (o máximo do intervalo).
 */
export const suggestSet = (exercise, setIndex) => {
  const kind = trackingKind(exercise?.trackingType);
  const p = exercise?.prescription || {};
  const sets = [...workSets(exercise)].sort((a, b) => a.setIndex - b.setIndex);
  const previousToday = [...sets].reverse().find((s) => s.setIndex < setIndex) || sets[sets.length - 1] || null;
  const last = lastSetAt(exercise?.lastValues, setIndex) || (exercise?.lastValues?.sets || [])[0] || null;

  // A sugestão de progressão (ProgressionAdvisor, calculada da última sessão CONCLUÍDA) manda na primeira
  // série do dia — é para isso que existe. Antes era só texto: a série abria com o valor da última vez e a
  // progressão nunca acontecia. Nas séries seguintes manda o que o atleta acabou de fazer hoje (repetir o que
  // está a funcionar). `first-time` não traz valores (não se inventam cargas).
  const s = previousToday == null && exercise?.suggestion && exercise.suggestion.kind !== 'first-time'
    ? exercise.suggestion
    : null;

  const plannedReps = p.repsMax ?? p.repsMin ?? null;
  const reps = kind.usesTime ? null : (s?.reps ?? previousToday?.reps ?? last?.reps ?? plannedReps ?? 10);
  const seconds = kind.usesTime ? (s?.durationSeconds ?? previousToday?.durationSeconds ?? last?.durationSeconds ?? plannedReps ?? 30) : null;
  const weight = kind.usesWeight || kind.optionalWeight
    ? (s?.weightKg ?? previousToday?.weightKg ?? last?.weightKg ?? p.targetWeightKg ?? (kind.usesWeight ? 0 : null))
    : null;

  return { reps, weightKg: weight, durationSeconds: seconds };
};

/** O passo de ajuste da carga (kg): 1 até aos 20, 2,5 até aos 100, 5 acima (os saltos que existem nos pesos dos ginásios). */
export const weightStep = (kg) => { const n = Number(kg) || 0; return n >= 100 ? 5 : n >= 20 ? 2.5 : 1; };

export const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

/** Valida o que o sócio escreveu antes de enviar; devolve o corpo do pedido ou os erros em português. */
export const buildSetBody = (exercise, setIndex, draft, { clientUuid, performedAt }) => {
  const kind = trackingKind(exercise?.trackingType);
  const errors = [];
  const body = { clientUuid, setIndex, performedAt: performedAt || new Date().toISOString() };
  if (draft.isWarmup) body.isWarmup = true;   // aquecimento: gravado, mas fora do trabalho da sessão

  if (kind.usesTime) {
    const seconds = parseIntSafe(draft.durationSeconds);
    if (seconds === null || seconds < 1 || seconds > 86400) errors.push('Indique o tempo (1 s a 24 h).');
    else body.durationSeconds = seconds;
  } else {
    const reps = parseIntSafe(draft.reps);
    if (reps === null || reps < 1 || reps > 500) errors.push('Indique as repetições (1 a 500).');
    else body.reps = reps;
  }

  if (kind.usesWeight || (kind.optionalWeight && draft.weightKg !== null && draft.weightKg !== '' && draft.weightKg !== undefined)) {
    const kg = parseDecimal(draft.weightKg);
    if (kg === null) { if (kind.usesWeight) body.weightKg = 0; }
    else if (kg < 0 || kg > 1000) errors.push('A carga tem de estar entre 0 e 1000 kg.');
    else body.weightKg = Math.round(kg * 100) / 100;
  }

  if (draft.rpe !== null && draft.rpe !== undefined && draft.rpe !== '') {
    const rpe = parseDecimal(draft.rpe);
    if (rpe === null || rpe < 1 || rpe > 10 || (rpe * 2) % 1 !== 0) errors.push('O RPE vai de 1 a 10.');
    else body.rpe = rpe;
  }

  return { body, errors };
};

// ── progresso e resumo ─────────────────────────────────────────────────────────

export const isDone = (exercise) => exercise?.status === 'Completed' || exercise?.status === 'Skipped';

/** O exercício conta como "feito para avançar" quando tem todas as séries previstas registadas (as de trabalho). */
export const hasAllSets = (exercise) => {
  const target = exercise?.prescription?.setsTarget;
  return target != null && workSets(exercise).length >= target;
};

export const progressOf = (exercises) => {
  const total = exercises.length;
  const completed = exercises.filter((e) => e.status === 'Completed').length;
  const skipped = exercises.filter((e) => e.status === 'Skipped').length;
  const setsLogged = exercises.reduce((n, e) => n + workSets(e).length, 0);
  const setsTarget = exercises.reduce((n, e) => n + (e.prescription?.setsTarget || 0), 0);
  return {
    total, completed, skipped, setsLogged, setsTarget,
    remaining: total - completed - skipped,
    percent: total === 0 ? 0 : Math.round((100 * (completed + skipped)) / total),
  };
};

/** O primeiro exercício por fazer a partir de um ponto (ou desde o princípio); -1 se não houver. */
export const firstOpenIndex = (exercises, from = 0) => {
  for (let i = 0; i < exercises.length; i += 1) {
    const idx = (from + i) % exercises.length;
    if (!isDone(exercises[idx])) return idx;
  }
  return -1;
};

/** Σ carga × repetições das séries de TRABALHO com carga (kg). Aquecimento fora: não é volume da sessão. */
export const volumeKg = (exercises) =>
  Math.round(exercises.reduce((sum, e) => sum + workSets(e).reduce((s, set) => s + ((set.weightKg || 0) * (set.reps || 0)), 0), 0));

/** A melhor série de um exercício, para o resumo final: a de maior carga (ou mais repetições/tempo), só de trabalho. */
export const bestSet = (exercise) => {
  const sets = workSets(exercise);
  if (sets.length === 0) return null;
  const kind = trackingKind(exercise.trackingType);
  const score = (s) => (kind.usesTime ? (s.durationSeconds || 0) : ((s.weightKg || 0) * 1000 + (s.reps || 0)));
  return sets.reduce((best, s) => (score(s) > score(best) ? s : best), sets[0]);
};

export const describeSet = (set, trackingType) => {
  if (!set) return '';
  const kind = trackingKind(trackingType);
  if (kind.usesTime) return formatClock(set.durationSeconds || 0);
  const kg = set.weightKg != null && set.weightKg > 0 ? `${formatKg(set.weightKg)} kg × ` : '';
  return `${kg}${set.reps}`;
};

/**
 * Quanto demora o treino, em minutos (arredondado a 5): por cada série o tempo a fazê-la (um exercício por tempo usa o seu tempo; os outros
 * ~40 s), mais o descanso entre séries, mais um minuto para trocar de exercício.
 */
export const estimateMinutes = (exercises) => {
  let seconds = 0;
  for (const e of exercises) {
    const p = e.prescription || {};
    const kind = trackingKind(e.trackingType);
    const sets = p.setsTarget || 3;
    const work = kind.usesTime ? (p.repsMax ?? p.repsMin ?? 45) : 40;
    seconds += sets * work + Math.max(0, sets - 1) * (p.restSeconds ?? 60) + 60;
  }
  return Math.max(5, Math.round(seconds / 60 / 5) * 5);
};

// ── media ──────────────────────────────────────────────────────────────────────

/** Só se mostra o que se sabe seguro: caminhos do próprio servidor, ou https. Nunca javascript:, data: nem http aberto. */
export const safeMediaUrl = (url) => {
  if (typeof url !== 'string') return null;
  const u = url.trim();
  if (u === '') return null;
  if (u.startsWith('//')) return null;
  if (u.startsWith('/')) return u;
  return /^https:\/\/[^\s]+$/i.test(u) ? u : null;
};

const VIDEO_EXT = /\.(mp4|webm|m4v|mov)(\?.*)?$/i;
const ANIM_EXT = /\.(gif|apng)(\?.*)?$/i;

/** Classifica um ficheiro de media: video | embed (player de outra plataforma) | animation | photo. */
export const mediaKindOf = (item) => {
  const url = (item?.url || '').toLowerCase();
  const kind = (item?.kind || '').toLowerCase();
  if (kind === 'embed') return 'embed';
  if (kind === 'video' || VIDEO_EXT.test(url)) return 'video';
  if (kind === 'animation' || kind === 'gif' || ANIM_EXT.test(url)) return 'animation';
  // Um endereço de incorporação que chegasse sem tipo (dados antigos) não pode cair para «imagem» e partir a lista.
  if (/^https:\/\/(www\.)?youtube(-nocookie)?\.com\/embed\//i.test(url)) return 'embed';
  return 'photo';
};

/** O endereço público do vídeo (o que se mostra a quem não tem rede), a partir do id guardado no servidor. */
const watchUrlOf = (item) => {
  const provider = (item?.provider || '').toLowerCase();
  const id = item?.providerVideoId || item?.videoId;
  if (provider !== 'youtube' || !id) return null;
  return safeMediaUrl(`https://www.youtube.com/watch?v=${encodeURIComponent(id)}`);
};

/**
 * A demonstração de um exercício, no formato que o overlay consome — um só, venha de onde vier. Hoje junta o que a biblioteca (e o próprio
 * exercício) já têm; a futura biblioteca de demonstrações Gymag entra por um "fornecedor" (ver demoRegistry.js) sem mudar o overlay.
 */
export const buildDemo = (exercise, library = exercise?.library) => {
  const media = [];
  const seen = new Set();
  const add = (item) => {
    const url = safeMediaUrl(item.url);
    if (!url || seen.has(url)) return;
    seen.add(url);
    media.push({
      kind: mediaKindOf({ ...item, url }), url, alt: item.alt || item.altText || null, poster: safeMediaUrl(item.poster) || null, source: item.source || 'library',
      attribution: item.attribution || null, origin: item.origin || null, originLabel: item.originLabel || null,
      // Vídeo incorporado: quem o serve, o id e o endereço público. Sem isto não há como dizer «abre no YouTube».
      provider: item.provider || null, videoId: item.providerVideoId || item.videoId || null, channel: item.channelName || null,
      watchUrl: watchUrlOf(item),
    });
  };

  // Só demonstrações APROVADAS (o servidor já só envia essas; isto é a segunda guarda): pendente ou rejeitada nunca se mostra como demonstração válida.
  (library?.media || [])
    .filter((m) => !m.validationStatus || m.validationStatus === 'approved')
    .forEach((m) => add({
      url: m.url, kind: m.kind, alt: m.altText, source: m.source || 'library', attribution: m.attribution, origin: m.origin, originLabel: m.originLabel,
      provider: m.provider, providerVideoId: m.providerVideoId, channelName: m.channelName,
    }));
  if (exercise) {
    if (VIDEO_EXT.test(exercise.videoUrl || '')) add({ url: exercise.videoUrl, kind: 'video' });   // uma página de vídeo (não um ficheiro) não se incorpora
    add({ url: exercise.gifUrl, kind: 'animation' });
    add({ url: exercise.imageUrl, kind: 'photo' });
  }
  if (library) {
    if (VIDEO_EXT.test(library.videoUrl || '')) add({ url: library.videoUrl, kind: 'video' });
    add({ url: library.gifUrl, kind: 'animation' });
    add({ url: library.imageUrl, kind: 'photo' });
  }

  // Um vídeo sem póster próprio usa a fotografia do exercício: aparece enquanto o vídeo carrega, e é o que se vê com "movimento reduzido".
  const firstPhoto = media.find((m) => m.kind === 'photo')?.url || null;
  media.forEach((m) => { if (m.kind === 'video' && !m.poster) m.poster = firstPhoto; });

  // A PRIORIDADE, no cliente, igual à do servidor: um vídeo PRÓPRIO aprovado ganha sempre ao incorporado. Se ambos
  // chegaram, o do YouTube sai da lista — o atleta não pode ver o de terceiros quando o ginásio filmou o seu.
  const hasOwnVideo = media.some((m) => m.kind === 'video' && m.source !== 'embed');
  const usable = hasOwnVideo ? media.filter((m) => m.kind !== 'embed') : media;

  // Uma ligação a uma página de vídeo (não um ficheiro): nunca se incorpora; abre-se por escolha do sócio.
  const external = [exercise?.videoUrl, library?.videoUrl].map(safeMediaUrl).find((u) => u && !VIDEO_EXT.test(u) && /^https:/i.test(u)) || null;

  return {
    title: exercise?.name || library?.name || '',
    media: usable.sort((a, b) => ({ video: 0, embed: 1, animation: 2, photo: 3 }[a.kind] - { video: 0, embed: 1, animation: 2, photo: 3 }[b.kind])),
    externalVideoUrl: external,
    steps: library?.steps || [],
    mistakes: library?.mistakes || [],
    tip: library?.tip || null,
    muscles: library?.muscles || [],
    equipment: library?.equipment || [],
    level: library?.level || null,
    mechanic: library?.mechanic || null,
  };
};

/** O sócio pediu "movimento reduzido" ao sistema? Então nenhum vídeo arranca sozinho. */
export const prefersReducedMotion = () => {
  try { return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
};

export const hasDemoContent = (demo) => !!demo && (demo.media.length > 0 || demo.steps.length > 0 || demo.mistakes.length > 0 || !!demo.tip || !!demo.externalVideoUrl);

/** O que se mostra na área grande do exercício: a fotografia, se houver; senão a animação; senão (só) o primeiro fotograma de um vídeo. A animação esquemática vem no «Ver como se faz», não no lugar da foto. */
export const heroMedia = (demo) => demo?.media.find((m) => m.kind === 'photo') || demo?.media.find((m) => m.kind === 'animation') || demo?.media.find((m) => m.kind === 'video') || null;

export const muscleNames = (library, role) =>
  (library?.muscles || []).filter((m) => !role || m.role === role).map((m) => m.name);

// ── alternativas ───────────────────────────────────────────────────────────────

const KIND_RANK = { planned: 0, equivalent: 1, variation: 2, regression: 3, progression: 3, 'no-machine': 4 };

/**
 * A sugestão veio do MOTOR (equivalente proposto) e não de uma pessoa da biblioteca? O servidor marca assim as suas
 * propostas (`source: 'auto'`); as curadas dizem outra coisa (ou nada, nos dados antigos).
 */
export const isSuggestedByEngine = (alternative) => alternative?.source === 'auto';

/**
 * A alternativa a destacar como «melhor»: primeiro as CURADAS disponíveis (escolhidas por uma pessoa); só quando não
 * houver nenhuma curada disponível é que a melhor proposta do motor pode ocupar esse lugar. O exercício do plano (a que
 * se volta atrás depois de uma troca) nunca é «melhor alternativa».
 */
export const bestAlternativeId = (alternatives) => {
  const usable = (alternatives || []).filter((a) => a?.available && !a.isPlanned);
  const pick = usable.find((a) => !isSuggestedByEngine(a)) || usable.find(isSuggestedByEngine);
  return pick?.libraryExerciseId ?? null;
};

/** Disponíveis primeiro, depois pelo tipo (equivalente antes de variação…), mantendo a ordem do servidor (prioridade) dentro de cada grupo. */
export const sortAlternatives = (alternatives) =>
  alternatives
    .map((a, i) => ({ a, i }))
    .sort((x, y) => (Number(y.a.available) - Number(x.a.available))
      || ((KIND_RANK[x.a.kind] ?? 5) - (KIND_RANK[y.a.kind] ?? 5))
      || (x.i - y.i))
    .map(({ a }) => a);

export const SUBSTITUTE_REASONS = ['Máquina ocupada', 'Sem equipamento', 'Desconforto ou dor', 'Prefiro este'];

// ── a fila local (séries e estados que ainda não chegaram ao servidor) ─────────

/** Um GUID; usa o do navegador quando existe. */
export const newUuid = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const hex = (n) => Array.from({ length: n }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${['8', '9', 'a', 'b'][Math.floor(Math.random() * 4)]}${hex(3)}-${hex(12)}`;
};

export const STORAGE_PREFIX = 'gymag.wp.v1.';

// Tudo o que o player guarda no telemóvel (fila por entregar, exercício em que ia, descanso) é DE QUEM TREINOU: num aparelho partilhado,
// o sócio B nunca vê nem entrega o que o sócio A deixou por enviar (o servidor recusaria, mas A perdia os registos). O âmbito é o id do utilizador.
let storageScope = '';
export const setStorageScope = (userId) => { storageScope = userId === null || userId === undefined || userId === '' ? '' : `u${userId}.`; };
export const storageKey = (key) => `${STORAGE_PREFIX}${storageScope}${key}`;

export const safeStorage = {
  get(key) { try { return JSON.parse(localStorage.getItem(storageKey(key)) || 'null'); } catch { return null; } },
  set(key, value) { try { localStorage.setItem(storageKey(key), JSON.stringify(value)); } catch { /* sem armazenamento: o player funciona na mesma */ } },
  remove(key) { try { localStorage.removeItem(storageKey(key)); } catch { /* idem */ } },
};

/**
 * Operações que se podem repetir sem efeito duplo no servidor — por isso é seguro guardá-las e reenviá-las:
 * a série (ClientUuid), concluir e saltar (repetidos devolvem o mesmo estado). Tudo o resto (substituir, concluir e abandonar o treino)
 * exige ligação e é feito na hora, depois de a fila esvaziar.
 */
export const QUEUEABLE = ['set', 'complete-exercise', 'skip-exercise'];

export const isNetworkError = (error) => !!error && !error.response;

/** 4xx (menos 408/429) é uma resposta definitiva do servidor: reenviar não a muda. */
export const isPermanentFailure = (error) => {
  const status = error?.response?.status;
  return !!status && status >= 400 && status < 500 && status !== 408 && status !== 429;
};

export const errorMessage = (error, fallback = 'Não foi possível concluir. Tente outra vez.') => {
  const data = error?.response?.data;
  if (Array.isArray(data?.errors) && data.errors.length) return data.errors[0];
  return data?.message || (isNetworkError(error) ? 'Sem ligação à internet.' : fallback);
};

/**
 * O treino como o sócio o vê: o que o servidor tem + o que a fila ainda não entregou.
 * Uma série na fila aparece como "por sincronizar" (pending) até o servidor a confirmar; concluir/saltar na fila já se mostram.
 */
export const applyQueue = (exec, queue) => {
  if (!exec || !queue || queue.length === 0) return exec;
  const exercises = exec.exercises.map((e) => ({ ...e, sets: [...(e.sets || [])] }));
  const byId = new Map(exercises.map((e) => [e.id, e]));

  for (const op of queue) {
    if (op.executionId !== exec.executionId) continue;
    const ex = byId.get(op.exerciseId);
    if (!ex) continue;
    if (op.type === 'set') {
      if (ex.sets.some((s) => s.clientUuid === op.body.clientUuid || (s.setIndex === op.body.setIndex && !!s.isWarmup === !!op.body.isWarmup))) continue;
      ex.sets.push({
        id: `pending-${op.body.clientUuid}`, setIndex: op.body.setIndex, reps: op.body.reps || 0, weightKg: op.body.weightKg ?? null,
        durationSeconds: op.body.durationSeconds ?? null, rpe: op.body.rpe ?? null, performedAt: op.body.performedAt, clientUuid: op.body.clientUuid,
        isWarmup: !!op.body.isWarmup, pending: true,
      });
      ex.sets.sort((a, b) => a.setIndex - b.setIndex);
      if (!op.body.isWarmup) {
        if (ex.status === 'Pending') ex.status = 'InProgress';
        ex.canSubstitute = false;   // o servidor só deixa trocar um exercício sem séries: não prometer o que ele recusaria
      }
    } else if (op.type === 'complete-exercise' && ex.status !== 'Skipped') {
      ex.status = 'Completed';
    } else if (op.type === 'skip-exercise' && ex.status !== 'Completed') {
      ex.status = 'Skipped';
    }
  }

  const progress = progressOf(exercises);
  return {
    ...exec,
    exercises,
    progress: { ...exec.progress, exercisesTotal: progress.total, exercisesCompleted: progress.completed, exercisesSkipped: progress.skipped, exercisesRemaining: progress.remaining, setsLogged: progress.setsLogged, percent: progress.percent },
  };
};

/** Junta uma série confirmada pelo servidor ao treino em memória (substitui a pendente pelo mesmo clientUuid). */
export const mergeSavedSet = (exec, result) => {
  if (!exec || !result) return exec;
  const exercises = exec.exercises.map((e) => {
    if (e.id !== result.exerciseId) return e;
    const sets = (e.sets || []).filter((s) => s.clientUuid !== result.set.clientUuid && !(s.setIndex === result.set.setIndex && !!s.isWarmup === !!result.set.isWarmup));
    // O aviso de recorde vem com a confirmação do servidor (ele é quem sabe a história do atleta neste exercício):
    // fica preso à série, dentro do resultado, para o ecrã poder dizê-lo. Sem recorde não se acrescenta campo nenhum.
    sets.push({
      ...result.set,
      ...(result.isRecord ? { isRecord: true, previousBestOneRepMaxKg: result.previousBestOneRepMaxKg ?? null } : {}),
    });
    sets.sort((a, b) => a.setIndex - b.setIndex);
    return { ...e, sets, setsLogged: result.setsLogged, status: result.exerciseStatus || e.status, canSubstitute: false };
  });
  return { ...exec, exercises, progress: { ...exec.progress, ...(result.progress || {}) } };
};

/** Tira do treino em memória uma série que o servidor acabou de apagar (o resultado traz a série apagada e o estado depois de apagar). */
export const removeSavedSet = (exec, result) => {
  if (!exec || !result?.set) return exec;
  const exercises = exec.exercises.map((e) => {
    if (e.id !== result.exerciseId) return e;
    const sets = (e.sets || []).filter((s) => s.id !== result.set.id);
    return { ...e, sets, setsLogged: result.setsLogged ?? sets.length, status: result.exerciseStatus || e.status };
  });
  return { ...exec, exercises, progress: { ...exec.progress, ...(result.progress || {}) } };
};

/** Local date yyyy-MM-dd (para o servidor sugerir o dia da semana certo). */
export const localDateString = (d = new Date()) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** "1 série" / "3 séries": o português não perdoa o plural errado num ecrã que se lê a suar. */
export const plural = (n, one, many) => `${n} ${Number(n) === 1 ? one : many}`;

/**
 * Porque é que o dia foi sugerido — em palavras, e num só sítio.
 *
 * O servidor manda `suggestedBy` com três valores (`WorkoutExecutionService`): weekday, rotation,
 * first. Este mapa é usado no ecrã de hoje do player E no cabeçalho da sessão: quem entra no treino
 * pelo atalho («Começar», que salta o ecrã de hoje) tem de poder ver a mesma razão.
 */
export const WHY = {
  weekday: 'Sugerido pelo dia da semana',
  rotation: 'A seguir ao seu último treino',
  first: 'O primeiro dia do plano',
};

export const whyText = (suggestedBy) => WHY[suggestedBy] || null;

// ── a evolução desde a última vez (ecrã final) ─────────────────────────────────────────────
// Só se compara o que se pode comparar com verdade: o MESMO exercício, com carga, contra a última vez que o sócio o fez num treino concluído (lastValues).
// Sem última vez não há comparação (nunca se inventa uma subida).

const topWeight = (sets) => (sets || []).reduce((m, s) => Math.max(m, Number(s.weightKg) || 0), 0);
const repsAt = (sets, weight) => (sets || []).filter((s) => (Number(s.weightKg) || 0) === weight).reduce((m, s) => Math.max(m, Number(s.reps) || 0), 0);
const setsVolume = (sets) => (sets || []).reduce((n, s) => n + (Number(s.weightKg) || 0) * (Number(s.reps) || 0), 0);

/** O que mudou neste exercício face à última vez: { kind: 'weight'|'reps'|'time', delta, from, to } ou null (igual, pior, ou sem última vez). */
export const compareToLast = (exercise) => {
  const last = exercise?.lastValues;
  const sets = workSets(exercise);
  if (!last || !last.sets?.length || sets.length === 0 || exercise.status === 'Skipped') return null;
  const kind = trackingKind(exercise.trackingType);

  if (kind.usesTime) {
    const prev = last.sets.reduce((m, s) => Math.max(m, s.durationSeconds || 0), 0);
    const now = sets.reduce((m, s) => Math.max(m, s.durationSeconds || 0), 0);
    return now > prev && prev > 0 ? { kind: 'time', delta: now - prev, from: prev, to: now } : null;
  }

  const prevTop = last.topWeightKg != null ? Number(last.topWeightKg) : topWeight(last.sets);
  const nowTop = topWeight(sets);
  if (nowTop > 0 && prevTop > 0) {
    if (nowTop > prevTop) return { kind: 'weight', delta: Math.round((nowTop - prevTop) * 10) / 10, from: prevTop, to: nowTop };
    if (nowTop === prevTop) {
      const prevReps = repsAt(last.sets, prevTop); const nowReps = repsAt(sets, nowTop);
      return nowReps > prevReps ? { kind: 'reps', delta: nowReps - prevReps, from: prevReps, to: nowReps, weight: nowTop } : null;
    }
    return null;
  }
  // sem carga (repetições): mais repetições na melhor série
  const prevReps = last.sets.reduce((m, s) => Math.max(m, s.reps || 0), 0);
  const nowReps = sets.reduce((m, s) => Math.max(m, s.reps || 0), 0);
  return nowReps > prevReps && prevReps > 0 ? { kind: 'reps', delta: nowReps - prevReps, from: prevReps, to: nowReps } : null;
};

/** O volume deste treino contra o da última vez, só nos exercícios com carga que têm última vez. null se não há com que comparar. */
export const volumeVsLast = (exercises) => {
  let now = 0; let before = 0; let counted = 0;
  for (const e of exercises || []) {
    if (e.status === 'Skipped' || !e.lastValues?.sets?.length || !trackingKind(e.trackingType).usesWeight) continue;
    const v = setsVolume(workSets(e));
    const p = setsVolume(e.lastValues.sets);
    if (p <= 0) continue;
    now += v; before += p; counted += 1;
  }
  if (counted === 0 || before <= 0) return null;
  return { now: Math.round(now), before: Math.round(before), percent: Math.round(((now - before) / before) * 100), exercises: counted };
};

/** As melhorias a destacar no fim do treino: { name, text }, as maiores primeiro (carga, depois repetições, depois tempo). */
export const progressHighlights = (exercises, limit = 4) => {
  const order = { weight: 0, reps: 1, time: 2 };
  return (exercises || [])
    .map((e) => ({ e, c: compareToLast(e) }))
    .filter((x) => x.c)
    .sort((a, b) => order[a.c.kind] - order[b.c.kind] || b.c.delta - a.c.delta)
    .slice(0, limit)
    .map(({ e, c }) => ({
      name: e.name,
      text: c.kind === 'weight' ? `+${formatKg(c.delta)} kg (${formatKg(c.from)} → ${formatKg(c.to)} kg)`
        : c.kind === 'reps' ? `+${c.delta} repetições${c.weight ? ` com ${formatKg(c.weight)} kg` : ''}`
        : `+${c.delta} s a segurar`,
    }));
};
